import { Theme } from "@mui/material";
import { checkErrors, getDefaultOptions } from "../services/Category";
import { REQUIRED_LANGAGE_ID } from "./BannerEditor";
import {
  BannerFilterType,
  BannerStateType,
  BannerType,
  CriticityType,
  FormErrorsType,
  TipTapNode,
} from "./interfaces";

export const DEFAULT_FILTERS: BannerFilterType = {
  name: "",
  displayed: null,
  pinned: null,
  customers: [],
  criticity: null,
};

export const buildQueryParams = (
  filters: BannerFilterType
): URLSearchParams => {
  const params = new URLSearchParams({ page_size: "25" });
  if (filters.name) params.set("name", filters.name);
  if (filters.displayed !== null) {
    const isActive = filters.displayed.value === 1;
    params.set("is_active", isActive ? "true" : "false");
    if (!isActive) params.set("include_expired", "true");
  }
  if (filters.pinned !== null)
    params.set("is_pinned", filters.pinned.value === 1 ? "true" : "false");
  if (filters.criticity !== null)
    params.set("criticity", String(filters.criticity.value));
  const activeCustomers = filters.customers.filter((c) => c.id !== 0);
  activeCustomers.forEach((c) => params.append("customers", String(c.id)));
  return params;
};

export const MAX_CHARACTERS = 300;
const MAX_URL_LENGTH = 500;
const MAX_DEPTH = 6;
const MAX_NODES = 150;
const MAX_LINKS = 3;

const ALLOWED_MARKS = new Set(["bold", "italic", "underline", "link"]);

const ALLOWED_NODE_TYPES = new Set(["doc", "paragraph", "heading", "text"]);

function countNodes(node: TipTapNode, depth = 1, limit = MAX_NODES): number {
  if (depth > MAX_DEPTH + 10) return Infinity;

  let count = 1;
  if (!node.content) return count;

  for (const child of node.content) {
    count += countNodes(child, depth + 1, limit - count);
    if (count > limit) return Infinity;
  }

  return count;
}

function maxDepth(node: TipTapNode, currentDepth = 1): number {
  // Avoid stack overflow
  if (currentDepth > MAX_DEPTH + 10) return Infinity;
  if (!node.content) return currentDepth;
  return Math.max(
    ...node.content.map((child) => maxDepth(child, currentDepth + 1))
  );
}

function isWellFormedUrl(value: string): boolean {
  try {
    const url = new URL(value);

    // Avoid dangerous schema
    if (/^(javascript|data|vbscript):$/i.test(url.protocol)) {
      return false;
    }

    const allowedProtocols = ["http:", "https:"];
    if (!allowedProtocols.includes(url.protocol)) return false;

    const hostname = url.hostname;

    // Block domaine IDN (punycode)
    if (hostname.includes("xn--")) {
      return false;
    }

    // Only allowed ASCII (anti-homograph)
    if (!/^[a-z0-9.-]+$/i.test(hostname)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function areLinksValid(node: TipTapNode, depth = 1): boolean {
  // Avoid stack overflow
  if (depth > MAX_DEPTH + 10) return false;

  if (node.marks) {
    for (const mark of node.marks) {
      if (mark.type === "link") {
        const href = mark.attrs?.href;

        // Check if href exist
        if (typeof href !== "string") return false;

        // limit href size
        if (href.length > MAX_URL_LENGTH) return false;

        // Avoid empty href
        if (!href.trim()) return false;

        // URL format
        if (!isWellFormedUrl(href)) return false;
      }
    }
  }

  if (Array.isArray(node.content)) {
    return node.content.every((child) => areLinksValid(child, depth + 1));
  }

  return true;
}

function countLinks(node: TipTapNode, depth = 1, limit = MAX_LINKS): number {
  // Avoid stack overflow
  if (depth > MAX_DEPTH + 10) return Infinity;
  let count = node.marks?.filter((m) => m.type === "link").length ?? 0;

  if (count > limit) return Infinity;

  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      count += countLinks(child, depth + 1, limit - count);
      if (count > limit) return Infinity;
    }
  }

  return count;
}

// Check if text is not empty
function hasText(node: TipTapNode): boolean {
  if (node.type === "text") {
    return Boolean(node.text && node.text.trim().length > 0);
  }

  if (Array.isArray(node.content)) {
    return node.content.some(hasText);
  }

  return false;
}

// Count characters
function countCharacters(node: TipTapNode): number {
  let count = node.text?.length ?? 0;

  if (node.content) {
    count += node.content.reduce(
      (sum, child) => sum + countCharacters(child),
      0
    );
  }

  return count;
}

// Check Heading allowed level
function areHeadingsValid(node: TipTapNode): boolean {
  if (node.type === "heading") {
    const level = node.attrs?.level;
    if (typeof level !== "number" || level < 1 || level > 6) {
      return false;
    }
  }

  if (Array.isArray(node.content)) {
    return node.content.every(areHeadingsValid);
  }

  return true;
}

// Check Node type name validity
function areNodeTypesValid(node: TipTapNode): boolean {
  if (!ALLOWED_NODE_TYPES.has(node.type)) {
    return false;
  }

  if (Array.isArray(node.content)) {
    return node.content.every(areNodeTypesValid);
  }

  return true;
}

// Check Marks name validity
function areMarksValid(node: TipTapNode): boolean {
  if (node.marks) {
    for (const mark of node.marks) {
      if (!ALLOWED_MARKS.has(mark.type)) {
        return false;
      }
    }
  }

  if (Array.isArray(node.content)) {
    return node.content.every(areMarksValid);
  }

  return true;
}

// Check editor content
export function validateEditorContent(datas: BannerStateType) {
  if (!datas.content || Object.keys(datas.content).length === 0) {
    const errors: Record<string, string[]> = {};

    datas.languages.forEach((lang) => {
      errors[lang.id] = ["BANNER_CONTENT_MISSING"];
    });

    return { valid: false, errors: errors };
  }

  const errors: Record<string, string[]> = {};

  for (const [lang, doc] of Object.entries(datas.content)) {
    let errorsMsg = [];

    // Check if content is empty
    if (!hasText(doc)) errorsMsg.push("BANNER_ERROR_EMPTY_EDITOR");

    // Check character count
    const charCount = countCharacters(doc);
    if (charCount > MAX_CHARACTERS)
      errorsMsg.push("BANNER_ERROR_TOO_LONG_EDITOR");

    // Check node limit
    const nodeCount = countNodes(doc);
    if (nodeCount > MAX_NODES)
      errorsMsg.push(`BANNER_ERROR_TOO_COMPLEX_EDITOR`);

    // Check depth limit
    const depth = maxDepth(doc);
    if (depth > MAX_DEPTH) errorsMsg.push(`BANNER_ERROR_TOO_COMPLEX_EDITOR`);

    // Check link validity
    if (!areLinksValid(doc)) errorsMsg.push("BANNER_ERROR_INVALID_LINK_EDITOR");

    // Check links count
    const linksCount = countLinks(doc);
    if (linksCount > MAX_LINKS)
      errorsMsg.push("BANNER_ERROR_TOO_MANY_LINK_EDITOR");

    // Check heading level
    if (!areHeadingsValid(doc)) {
      errorsMsg.push("BANNER_ERROR_HEADING_INVALID_EDITOR");
    }

    // Check Node type name validity
    if (!areNodeTypesValid(doc)) {
      errorsMsg.push("BANNER_ERROR_NODE_TYPE_INVALID_EDITOR");
    }

    // Check Marks name validity
    if (!areMarksValid(doc)) {
      errorsMsg.push("BANNER_ERROR_MARKS_NAME_INVALID_EDITOR");
    }

    if (errorsMsg.length > 0) {
      errors[lang] = errorsMsg;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Validate banner options
 *
 * Check if all required options are filled
 */
export function validateForm(state: BannerStateType) {
  const errors: FormErrorsType = { hasError: false };

  // Check if name input is filled
  if (!state.name) {
    errors.hasError = true;
    errors.name = "BANNER_NAME_MISSING";
  }
  // Check if at least one language is selected
  if (!state.languages || state.languages.length === 0) {
    errors.hasError = true;
    errors.languages = "BANNER_LANGUAGES_MISSING";
  }
  // Check if english option is selected
  if (
    state.languages &&
    !state.languages.find((lang) => lang.id === REQUIRED_LANGAGE_ID)
  ) {
    errors.hasError = true;
    errors.languages = "BANNER_ENGLISH_LANGUAGES_MISSING";
  }
  // Check if a color/criticity is selected
  if (!(state.criticity === 0 || state.criticity)) {
    errors.hasError = true;
    errors.criticity = "BANNER_CRITICITY_MISSING";
  }
  // Check if display delay selector is filled if pinned banner checkbox is unchecked
  if (
    !state.is_pinned &&
    !(state.nb_days_before_redisplay === 0 || state.nb_days_before_redisplay)
  ) {
    errors.hasError = true;
    errors.nb_days_before_redisplay = "BANNER_DISPLAY_DELAY_MISSING";
  }
  // Check if all editor are filled
  if (state.languages && state.languages.length > 0) {
    if (!state.content || Object.keys(state.content).length === 0) {
      errors.hasError = true;
      errors.content = state.languages.map((lang) => ({
        id: lang.id,
        value: "BANNER_CONTENT_MISSING",
      }));
    } else if (state.languages.length !== Object.keys(state.content).length) {
      const content = Object.keys(state.content);
      const missingLanguagesContent = state.languages.filter(
        (lang) => !content.find((contentLang) => contentLang === lang.id)
      );
      errors.hasError = true;
      errors.content = missingLanguagesContent.map((lang) => ({
        id: lang.id,
        value: "BANNER_CONTENT_MISSING",
      }));
    }
  }

  return errors;
}

// Check link validity
export function isValidLinkHref(href: string): boolean {
  if (typeof href !== "string") return false;
  if (!href.trim()) return false;
  if (href.length > MAX_URL_LENGTH) return false;
  return isWellFormedUrl(href);
}

/**
 * Publish Banner
 */
export const publishBanner = async (
  bannerData: BannerStateType | BannerType,
  id?: number
) => {
  const url = id
    ? new URL(`/rbl/banners/${id}/`, window.origin)
    : new URL("/rbl/banners/", window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: id ? "PUT" : "POST",
      body: JSON.stringify(bannerData),
    })
  );

  checkErrors(response);

  return response.json();
};

export const deleteBanner = async (id: number) => {
  const response = await fetch(`/rbl/banners/${id}/`, {
    ...getDefaultOptions(),
    method: "DELETE",
  });

  checkErrors(response);

  // If delete success
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

/**
 * Generate banner background color depending criticity level
 *
 * @param theme
 * @param criticity
 */
export function getBannerBackgroundColor(
  theme: Theme,
  criticity: CriticityType
) {
  switch (criticity) {
    case 0:
      return theme.vars.palette.error.main;
    case 1:
      return theme.vars.palette.warning.main;
    case 2:
      return theme.vars.palette.nancie.gradient;
    case 3:
      return theme.vars.palette.success.main;
    default:
      return theme.vars.palette.success.main;
  }
}

/** Ink for `getBannerBackgroundColor`: each tone's contrast text, white on the Nancie gradient. */
export function getBannerTextColor(theme: Theme, criticity: CriticityType) {
  switch (criticity) {
    case 0:
      return theme.vars.palette.error.contrastText;
    case 1:
      return theme.vars.palette.warning.contrastText;
    case 2:
      return theme.vars.palette.common.white;
    default:
      return theme.vars.palette.success.contrastText;
  }
}
